// `BulkActions` — bulk action bar (US-145, US-146, US-148).
//
// Renders below the table toolbar when ≥1 lead is selected. Two actions:
//   - "Envoyer vers CRM"  → opens `<CRMExportModal>` for the selection.
//   - "Archiver"          → calls `useBulkArchiveLeads` + clears selection.

import { Archive, ArrowUpRight } from 'lucide-react';

import { useT } from '@barel/i18n';
import { Button, toast } from '@barel/ui';
import { useBulkArchiveLeads, useBulkUnarchiveLeads } from '@barel/api-client/react';

export interface BulkActionsProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onOpenCrm: () => void;
}

export function BulkActions({ selectedIds, onClearSelection, onOpenCrm }: BulkActionsProps) {
  const t = useT('leads');
  const archive = useBulkArchiveLeads();
  const unarchive = useBulkUnarchiveLeads();
  const count = selectedIds.length;

  if (count === 0) return null;

  const handleArchive = () => {
    // Snapshot the ids before clearing — undo needs them after the success
    // handler clears the selection set.
    const archivedIds = selectedIds.slice();
    archive.mutate(
      { ids: archivedIds },
      {
        onSuccess: ({ archived }) => {
          // Wave C1 (US-146) — 5-second undo toast. Clicking "Annuler"
          // re-runs `bulk-unarchive` with the snapshotted ids. The toast's
          // own duration controls the window; we don't need a separate
          // timer in component state.
          toast.success({
            title: t('toast.bulkArchived', { count: archived }),
            duration: 5000,
            action: {
              label: t('toast.undo'),
              onClick: () => {
                unarchive.mutate(
                  { ids: archivedIds },
                  {
                    onSuccess: () => {
                      toast.info({ title: t('toast.bulkUnarchived', { count: archived }) });
                    },
                    onError: () => {
                      toast.error({ title: t('toast.bulkArchiveError') });
                    },
                  },
                );
              },
            },
          });
          onClearSelection();
        },
        onError: () => {
          toast.error({ title: t('toast.bulkArchiveError') });
        },
      },
    );
  };

  return (
    <div
      data-testid="leads-bulk-bar"
      role="toolbar"
      aria-label={t('bulk.label')}
      className="flex items-center gap-3 border-b border-default bg-accent-dim px-5 py-2"
    >
      <span className="text-xs font-semibold text-accent">
        {t('list.selectedCount', { count })}
      </span>
      <button
        type="button"
        onClick={onClearSelection}
        className="text-2xs text-tertiary hover:text-secondary"
      >
        {t('bulk.clearSelection')}
      </button>
      <div className="flex-1" />
      <Button
        variant="primary"
        size="sm"
        iconLeft={<ArrowUpRight size={14} aria-hidden="true" />}
        onClick={onOpenCrm}
        data-testid="leads-bulk-crm"
      >
        {t('bulk.sendToCrm')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<Archive size={14} aria-hidden="true" />}
        onClick={handleArchive}
        disabled={archive.isPending}
        data-testid="leads-bulk-archive"
      >
        {t('bulk.archive')}
      </Button>
    </div>
  );
}
